import type {IAugmentedJQuery, ILogService, IRootScopeService} from 'angular';
import register from '@kpsys/angularjs-register';
import {isPromise} from 'shared/utils/custom-utils';
import {debounce} from 'lodash/fp';

function isNgDebounceOptions(object: unknown): object is NgDebounceOptions {
    return !!object && (typeof (object as NgDebounceOptions).delay) === 'number';
}

// eslint-disable-next-line
let _$injector: angular.auto.IInjectorService;

/**
 * This function must be inserted to `run` method of `angular.module`.
 * It is necessary for correct running of `@ngDecorators`.
 *
 * @param {IInjectorService} $injector
 *
 * @example
 * ```
 *      angular.module('myModule', []).run(initNgDecoratorsHelper);
 * ```
 */
export function initNgDecoratorsHelper($injector: angular.auto.IInjectorService) {
    'ngInject';

    _$injector = $injector;
}

// eslint-disable-next-line
export const __ngAsyncDecoratorInternalAPI = {
    clearInjector: () => {
        _$injector = undefined;
    }
};

/**
 * Decorator, which triggers digest cycle when promise from decorated promise-returning function is fulfilled or rejected.
 * It is for async functions.
 */
export function ngAsync() {
    return function ngAsyncFunction(target, key, descriptor) {

        const fn = descriptor.value;
        descriptor.value = function(...args) {
            if (!_$injector) {
                throw new TypeError('Cannot apply @ngAsync decorator. Probably \'ngDecoratorsHelper\' is not registered as dependent module.');
            }

            const result = fn.apply(this, args);

            if (isPromise(result)) {
                _$injector.invoke(/*@ngInject*/ ($rootScope: IRootScopeService) => {

                    const applyRootScope = (value) => {
                        $rootScope.$applyAsync();
                        return value;
                    };

                    result.then(applyRootScope, applyRootScope);
                });
            } else {
                _$injector.invoke(/*@ngInject*/ ($log: ILogService) =>
                    $log.warn('Result is not Promise-like value. @ngAsync decorator is not applied.'));
            }

            return result;
        };
    };
}

export interface NgDebounceOptions {
    delay: number;
    apply?: boolean;
}

export function ngDebounce(options: number | NgDebounceOptions) {
    return function ngDebounceFunction(target, key, descriptor: PropertyDescriptor) {

        let debounceTime: number;
        let apply = true;

        if (isNgDebounceOptions(options)) {
            debounceTime = options.delay;
            apply = options.apply;
        } else {
            debounceTime = options;
        }

        const originalFunction = descriptor.value;

        function wrapperFunction(...args) {
            if (!_$injector) {
                throw new TypeError('Cannot apply @ngDebounce decorator. Probably \'ngDecoratorsHelper\' is not registered as dependent module.');
            }

            if (apply) {
                _$injector.invoke(/*@ngInject*/($rootScope: IRootScopeService) => {

                    $rootScope.$applyAsync();
                });
            }

            // eslint-disable-next-line no-invalid-this
            return originalFunction.apply(this, args);
        }

        descriptor.value = debounce(debounceTime, wrapperFunction);
    };
}

export function ngComponentCssClass(cssClass: string): PropertyDecorator {
    return function ngComponentCssClassFunction(target, propertyKey: string) {
        if (['template', 'templateUrl'].includes(propertyKey)) {
            let value = target[propertyKey];

            if (delete target[propertyKey]) {
                Object.defineProperty(target, propertyKey, {
                    enumerable: true,
                    configurable: true,
                    set: (v) => value = v,
                    get: () => /*@ngInject*/ function injectCssClassFunction($element: IAugmentedJQuery): string {

                        $element.addClass(cssClass);

                        return value;
                    }
                });
            }
        } else {
            throw new TypeError('@ngComponentCssClass decorator can be used only on `template` or `templateUrl` property in angular component definition');
        }
    };
}

/**
 * @ngdoc module
 * @module ngDecoratorsHelper
 * @name ngDecoratorsHelper
 *
 * Module for correct function of @ngDecorators()
 */
export default register('ngDecoratorsHelper')
    .run(initNgDecoratorsHelper)
    .name();
